/* <floodie-gallery> — auto-advancing media gallery. Vanilla custom element, no libs.
   Cross-fade + Ken Burns (transform/opacity only), progress-bar tabs, full pause matrix
   (hover, focus, interaction, off-screen, hidden tab, reduced-motion), swipe, keyboard.
   One shared rAF ticker; only the gallery nearest viewport centre plays. */
(function () {
  'use strict';
  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var registry = [];
  var rafId = null, lastNearest = 0, cardTick = null;

  function loop(ts) {
    if (ts - lastNearest > 400) { nearest(); lastNearest = ts; }
    for (var i = 0; i < registry.length; i++) registry[i]._tick(ts);
    if (cardTick) cardTick(ts);
    rafId = requestAnimationFrame(loop);
  }
  function start() { if (!rafId && (registry.length || cardTick)) rafId = requestAnimationFrame(loop); }
  function nearest() {
    var c = window.innerHeight / 2, best = null, bd = Infinity;
    registry.forEach(function (g) {
      if (!g._inView) { g._nearest = false; return; }
      var r = g.getBoundingClientRect(), d = Math.abs((r.top + r.bottom) / 2 - c);
      if (d < bd) { bd = d; best = g; }
    });
    registry.forEach(function (g) { g._nearest = (g === best); });
  }

  function FG() { return Reflect.construct(HTMLElement, [], FG); }
  FG.prototype = Object.create(HTMLElement.prototype);
  FG.prototype.constructor = FG;

  FG.prototype.connectedCallback = function () {
    if (this._init) return; this._init = true;
    var g = this;
    g.slides = Array.prototype.slice.call(g.querySelectorAll('[data-fg-slide]'));
    if (g.slides.length < 2) return;
    g.iv = +g.getAttribute('interval') || 4000;
    g.tr = +g.getAttribute('transition') || 700;
    g.kb = g.getAttribute('kenburns') !== 'false' && !REDUCED;
    g.kbScale = +g.getAttribute('kb-scale') || 1.04;
    g.resumeAfter = +g.getAttribute('resume-after') || 0;
    g.loopOn = g.getAttribute('loop') !== 'false';
    g.idx = 0; g.elapsed = 0; g._last = 0;
    g.playing = g.getAttribute('autoplay') !== 'false' && !REDUCED;
    g.userStopped = false; g._inView = false; g._nearest = false;
    g.classList.add('fg-ready');
    g.style.setProperty('--fg-t', g.tr + 'ms');
    g.style.setProperty('--fg-kb', g.kbScale);
    var sp = g.slides[0].parentElement;
    if (/^(UL|OL)$/.test(sp.tagName)) sp.setAttribute('role', 'presentation'); /* li roles below strip list semantics */
    g.slides.forEach(function (s, i) {
      s.classList.add('fg-slide');
      if (i === 0) g._activate(s, 0);
      s.setAttribute('role', 'group');
      s.setAttribute('aria-roledescription', 'slide');
      s.setAttribute('aria-label', (i + 1) + ' of ' + g.slides.length);
    });
    g.setAttribute('role', 'region');
    g.setAttribute('aria-roledescription', 'carousel');
    if (!g.getAttribute('aria-label')) g.setAttribute('aria-label', 'Product gallery');

    /* live region */
    g._live = document.createElement('p');
    g._live.className = 'visually-hidden'; g._live.setAttribute('aria-live', 'polite');
    g.appendChild(g._live);

    /* controls: progress bars + pause */
    if (g.getAttribute('controls') !== 'none') {
      var bars = document.createElement('div');
      bars.className = 'fg-bars'; bars.setAttribute('role', 'tablist');
      g.bars = g.slides.map(function (s, i) {
        var b = document.createElement('button');
        b.type = 'button'; b.className = 'fg-bar'; b.setAttribute('role', 'tab');
        b.setAttribute('aria-label', 'Show image ' + (i + 1));
        b.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
        b.innerHTML = '<span></span>';
        b.addEventListener('click', function () { g._userTakeover(); g.goTo(i); });
        bars.appendChild(b); return b;
      });
      g.appendChild(bars);
      var p = document.createElement('button');
      p.type = 'button'; p.className = 'fg-pause';
      p.setAttribute('aria-label', 'Pause slideshow');
      p.innerHTML = '&#10074;&#10074;';
      p.addEventListener('click', function () {
        if (g.playing) { g.playing = false; g.userStopped = true; p.innerHTML = '&#9654;'; p.setAttribute('aria-label', 'Play slideshow'); }
        else { g.playing = true; g.userStopped = false; g.elapsed = 0; p.innerHTML = '&#10074;&#10074;'; p.setAttribute('aria-label', 'Pause slideshow'); }
      });
      g._pauseBtn = p; g.appendChild(p);
    }

    /* pause matrix */
    if (g.getAttribute('pause-on-hover') !== 'false') {
      g.addEventListener('pointerenter', function () { g._hover = true; });
      g.addEventListener('pointerleave', function () { g._hover = false; });
    }
    g.addEventListener('focusin', function () { g._focus = true; });
    g.addEventListener('focusout', function () { g._focus = false; });
    /* click on a slide (e.g. opening the theme lightbox) pauses; stays paused on close */
    g.addEventListener('click', function (e) {
      if (e.target.closest('.fg-pause,.fg-bar')) return;
      if (e.target.closest('[data-fg-slide]')) g._userTakeover();
    });
    new IntersectionObserver(function (e) { g._inView = e[0].isIntersecting; }, { threshold: 0.25 }).observe(g);

    /* swipe — pan-y preserved */
    var sx = null, sy = null;
    g.addEventListener('pointerdown', function (e) { sx = e.clientX; sy = e.clientY; }, { passive: true });
    g.addEventListener('pointerup', function (e) {
      if (sx === null) return;
      var dx = e.clientX - sx, dy = e.clientY - sy; sx = null;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) { g._userTakeover(); g.goTo(g.idx + (dx < 0 ? 1 : -1)); }
    }, { passive: true });

    /* keyboard */
    g.tabIndex = g.tabIndex || 0;
    g.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') { e.preventDefault(); g._userTakeover(); g.goTo(g.idx + 1); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); g._userTakeover(); g.goTo(g.idx - 1); }
      if (e.key === ' ') { e.preventDefault(); if (g._pauseBtn) g._pauseBtn.click(); }
    });

    registry.push(g); start();
  };

  FG.prototype._userTakeover = function () {
    var g = this;
    if (g.resumeAfter > 0) {
      g.playing = false; clearTimeout(g._rt);
      g._rt = setTimeout(function () { if (!g.userStopped) { g.playing = true; g.elapsed = 0; } }, g.resumeAfter);
    } else { g.playing = false; g.userStopped = true; if (g._pauseBtn) { g._pauseBtn.innerHTML = '&#9654;'; g._pauseBtn.setAttribute('aria-label', 'Play slideshow'); } }
  };

  FG.prototype._activate = function (s, dir) {
    s.classList.add('fg-on');
    if (this.kb) {
      var m = s.querySelector('img,video');
      if (m) { m.style.animation = 'none'; void m.offsetWidth;
        m.style.animation = (dir % 2 ? 'fgKbOut ' : 'fgKbIn ') + (this.iv + this.tr) + 'ms linear forwards'; }
    }
  };

  FG.prototype.goTo = function (n, announce) {
    var g = this, len = g.slides.length;
    n = g.loopOn ? (n + len) % len : Math.max(0, Math.min(len - 1, n));
    if (n === g.idx) return;
    var out = g.slides[g.idx], inn = g.slides[n];
    var mi = inn.querySelector('img'); if (mi) { mi.loading = 'eager'; }
    var nx = g.slides[(n + 1) % len].querySelector('img'); if (nx) nx.loading = 'eager';
    out.style.willChange = 'opacity,transform'; inn.style.willChange = 'opacity,transform';
    inn.classList.add('fg-on'); g._activate(inn, n);
    out.classList.remove('fg-on');
    setTimeout(function () { out.style.willChange = ''; inn.style.willChange = ''; }, g.tr + 60);
    g.idx = n; g.elapsed = 0;
    if (g.bars) g.bars.forEach(function (b, i) { b.setAttribute('aria-selected', i === n ? 'true' : 'false'); var sp = b.firstChild; if (i < n) sp.style.transform = 'scaleX(1)'; else if (i > n) sp.style.transform = 'scaleX(0)'; });
    if (g._live && announce !== false) g._live.textContent = 'Image ' + (n + 1) + ' of ' + len;
    g.dispatchEvent(new CustomEvent('fg:change', { bubbles: true, detail: { index: n, slide: inn } }));
  };

  FG.prototype._tick = function (ts) {
    var g = this;
    if (!g._last) { g._last = ts; return; }
    var dt = ts - g._last; g._last = ts;
    if (!g.playing || g.userStopped || g._hover || g._focus || document.hidden || !g._inView || !g._nearest) return;
    g.elapsed += dt;
    if (g.bars) { var sp = g.bars[g.idx].firstChild; sp.style.transform = 'scaleX(' + Math.min(1, g.elapsed / g.iv) + ')'; }
    if (g.elapsed >= g.iv) g.goTo(g.idx + 1);
  };

  customElements.define('floodie-gallery', FG);

  /* -------- deployment 1: adopt Ritual PDP media gallery -------- */
  function adoptPDP() {
    var grid = document.querySelector('.media-gallery__grid');
    if (!grid || grid.closest('floodie-gallery')) return;
    var el = document.createElement('floodie-gallery');
    var cfg = document.querySelector('[data-fg-pdp]');
    if (cfg) ['interval', 'transition', 'kenburns', 'autoplay', 'resumeAfter'].forEach(function (k) {
      if (cfg.dataset[k]) el.setAttribute(k.replace(/[A-Z]/g, function (c) { return '-' + c.toLowerCase(); }), cfg.dataset[k]);
    });
    el.setAttribute('aria-label', 'Product images');
    el.className = 'fg-adopt';
    var parent = grid.parentNode, next = grid.nextSibling;
    Array.prototype.forEach.call(grid.children, function (li, i) {
      li.setAttribute('data-fg-slide', '');
      var img = li.querySelector('img');
      if (img && i > 0) img.loading = 'lazy';
      if (img && i === 1) (window.requestIdleCallback || window.setTimeout)(function () { img.loading = 'eager'; });
    });
    el.appendChild(grid);          /* build while detached... */
    parent.insertBefore(el, next); /* ...connectedCallback now sees the slides */
  }

  /* variant change: reset to slide 1 and restart autoplay */
  function variantReset() {
    document.addEventListener('change', function (e) {
      if (!e.target.closest('form[action^="/cart/add"],variant-picker,[class*="variant-picker"]')) return;
      registry.forEach(function (g) {
        if (!g.classList.contains('fg-adopt')) return;
        g.goTo(0);
        if (!REDUCED) { g.userStopped = false; g.playing = true; g.elapsed = 0;
          if (g._pauseBtn) { g._pauseBtn.innerHTML = '&#10074;&#10074;'; g._pauseBtn.setAttribute('aria-label', 'Pause slideshow'); } }
      });
    });
  }

  /* -------- deployment 2: collection cards --------
     Desktop hover preview is Ritual's own server-rendered card slideshow (no extra JS).
     Mobile (no hover): ambient-cycle ONLY the card nearest viewport centre, every 3s,
     via the theme Slideshow's public next() — same shared rAF loop, no controls. */
  function cards() {
    document.querySelectorAll('.product-card').forEach(function (c) {
      if (c.querySelectorAll('slideshow-slide, [ref="slides[]"]').length < 2) c.classList.add('fg-card-zoom');
    });
    if (!window.matchMedia('(hover:none)').matches || REDUCED) return;
    var shows = Array.prototype.slice.call(document.querySelectorAll('.product-card slideshow-component'));
    if (!shows.length) return;
    var last = 0;
    cardTick = function (ts) {
      if (ts - last < 3000) return;
      last = ts;
      if (document.hidden) return;
      var c = window.innerHeight / 2, best = null, bd = Infinity;
      shows.forEach(function (s) {
        var r = s.getBoundingClientRect();
        if (r.bottom < 0 || r.top > window.innerHeight) return;
        var d = Math.abs((r.top + r.bottom) / 2 - c);
        if (d < bd) { bd = d; best = s; }
      });
      if (best && typeof best.next === 'function') { try { best.next(); } catch (e) {} }
    };
    start();
  }

  function boot() { adoptPDP(); cards(); variantReset(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
})();
